// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { toSubscribedNotifications } from '@/account/api/external-user-notifications';
import { ExternalUser } from '@/account/api/external-user-types';
import {
  deleteExternalUser,
  listExternalUsers,
} from '@/account/api/external-users';
import { Organization } from '@/account/api/types';
import { ExternalUserFormDialog } from '@/account/components/external-user-form-dialog';
import { SupportSnackbar } from '@/components/support-snackbar';
import {
  AddRounded,
  DeleteRounded,
  EditRounded,
  MailOutlined,
} from '@/constants/hardfin-standard-icons';
import { useDisclosure } from '@/hooks/use-disclosure';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

interface Props {
  organization: Organization;
}

function DeleteConfirmationDialog({
  isOpen,
  onClose,
  onConfirm,
  externalUser,
  isLoading,
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  externalUser: ExternalUser | null;
  isLoading: boolean;
}) {
  if (!externalUser) {
    return null;
  }
  const label =
    externalUser.label != externalUser.email
      ? `${externalUser.label} (${externalUser.email})`
      : externalUser.email;
  return (
    <Dialog open={isOpen} onClose={onClose}>
      <DialogTitle>Delete custom notification</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Are you sure you want to delete custom notification {label}? This
          action cannot be undone.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button onClick={onConfirm} color="error" disabled={isLoading}>
          {isLoading ? <CircularProgress size={20} /> : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function OrganizationExternalUsersCard(props: Props) {
  const queryClient = useQueryClient();
  const addDialog = useDisclosure();
  const editDialog = useDisclosure();
  const deleteDialog = useDisclosure();
  const [selectedUser, setSelectedUser] = useState<ExternalUser | null>(null);

  const {
    data: externalUsers = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['external-users', props.organization.key],
    queryFn: () => listExternalUsers(props.organization.key),
  });

  const deleteMutation = useMutation({
    mutationFn: (userKey: string) =>
      deleteExternalUser(props.organization.key, userKey),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['external-users', props.organization.key],
      });
      deleteDialog.close();
      setSelectedUser(null);
    },
  });

  const handleEdit = (user: ExternalUser) => {
    setSelectedUser(user);
    editDialog.open();
  };

  const handleDelete = (user: ExternalUser) => {
    setSelectedUser(user);
    deleteDialog.open();
  };

  const handleDeleteConfirm = () => {
    if (selectedUser) {
      deleteMutation.mutate(selectedUser.key);
    }
  };

  if (error) {
    return (
      <Paper variant="outlined">
        <Box p={2}>
          <Typography color="error">
            Failed to load custom notification
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <>
      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        <Stack
          useFlexGap
          spacing={1}
          paddingX={2}
          paddingY={2}
          direction="row"
          alignItems="center"
          bgcolor="grey.50"
        >
          <Typography variant="overline" marginRight="auto">
            Custom notifications
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddRounded />}
            onClick={addDialog.open}
          >
            Add custom notification
          </Button>
        </Stack>
        <Divider />

        {isLoading ? (
          <Box display="flex" justifyContent="center" p={2}>
            <CircularProgress size={24} />
          </Box>
        ) : externalUsers.length === 0 ? (
          <Box p={2}>
            <Typography variant="body2" color="text.secondary" align="center">
              No custom notifications added yet
            </Typography>
          </Box>
        ) : (
          <Box p={2}>
            <Stack spacing={1}>
              {externalUsers.map((user) => {
                // Count active notifications
                const activeNotifications =
                  user.notifications?.filter(
                    (notif) => notif.activeChannels.length > 0
                  ).length || 0;

                return (
                  <Box
                    key={user.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 1.5,
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      '&:hover': {
                        bgcolor: 'grey.25',
                      },
                    }}
                  >
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
                    >
                      <Box
                        sx={(theme) => ({
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 28,
                          height: 28,
                          borderRadius: '50%',
                          border: `1px ${theme.palette.primary.main} solid`,
                          borderWidth: '1px',
                        })}
                      >
                        <MailOutlined fontSize="small" color="primary" />
                      </Box>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {user.email}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {activeNotifications} notification
                          {activeNotifications !== 1 ? 's' : ''} active
                        </Typography>
                      </Box>
                    </Box>

                    <Stack direction="row" spacing={0.5}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleEdit(user)}
                        startIcon={<EditRounded fontSize="small" />}
                        sx={{ py: 0.5, px: 1 }}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => handleDelete(user)}
                        startIcon={<DeleteRounded fontSize="small" />}
                        sx={(theme) => ({
                          py: 0.5,
                          px: 1,
                          color: theme.palette.action.active,
                        })}
                      >
                        Delete
                      </Button>
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          </Box>
        )}
      </Paper>

      <ExternalUserFormDialog
        isOpen={addDialog.isOpen}
        onClose={addDialog.close}
        organizationKey={props.organization.key}
        mode="create"
      />

      <ExternalUserFormDialog
        isOpen={editDialog.isOpen}
        onClose={() => {
          editDialog.close();
          setSelectedUser(null);
        }}
        organizationKey={props.organization.key}
        selectedUserKey={selectedUser?.key}
        initialData={
          selectedUser
            ? {
                email: selectedUser.email,
                notifications: toSubscribedNotifications(
                  selectedUser.notifications
                ),
              }
            : undefined
        }
        mode="edit"
      />

      <DeleteConfirmationDialog
        isOpen={deleteDialog.isOpen}
        onClose={() => {
          deleteDialog.close();
          setSelectedUser(null);
        }}
        onConfirm={handleDeleteConfirm}
        externalUser={selectedUser}
        isLoading={deleteMutation.isPending}
      />

      <SupportSnackbar
        isOpen={deleteMutation.isError}
        message="Failed to delete external user"
        onClose={deleteMutation.reset}
      />
    </>
  );
}
